<form [formGroup]="form" (ngSubmit)="onSubmit()">

  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Inputs</div>
  </p-divider>

  <p class="my-2">
    Select an image file in the file browser and create a pipeline of one or more image operations to apply.
  </p>

  <!-- ─── Operations ─────────────────────────────────────────────── -->
  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Operations</div>
  </p-divider>

  <!-- Add operation -->
  <div class="flex align-items-center gap-2 mb-3">
    <p-dropdown [options]="availableOperations"
                optionLabel="name"
                placeholder="Choose operation to add…"
                [(ngModel)]="operationToAdd"
                [ngModelOptions]="{standalone: true}"
                [group]="false"
                [filter]="true"
                filterBy="name"
                appendTo="body"
                styleClass="flex-1">
      <ng-template let-op pTemplate="item">
        <div class="flex align-items-center gap-2">
          <span class="op-engine-badge" [class]="getEngineBadgeClass(op.engineId)">
            {{getEngineBadge(op.engineId)}}
          </span>
          <span>{{op.name}}</span>
          <span class="text-color-secondary text-xs ml-auto op-category">{{op.category}}</span>
        </div>
      </ng-template>
    </p-dropdown>
    <p-button icon="pi pi-plus" pTooltip="Add to pipeline" tooltipPosition="bottom"
              size="small"
              (click)="addOperation()"
              [disabled]="!operationToAdd">
    </p-button>
  </div>

  <!-- Steps table -->
  <p-table [value]="steps"
           styleClass="p-datatable-sm steps-table"
           [scrollable]="true" scrollHeight="200px">
    <ng-template pTemplate="header">
      <tr>
        <th style="width:2rem">#</th>
        <th style="width:1.5rem"></th>
        <th>Operation</th>
        <th style="width:6rem">Engine</th>
        <th style="width:5.5rem">Actions</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-step let-ri="rowIndex">
      <tr [class.selected-step]="ri === selectedStepIndex"
          [class.disabled-step]="!step.enabled"
          (click)="selectStep(ri)">
        <td>{{ri + 1}}</td>
        <td>
          <i *ngIf="isRegionAware(step) && step.params['useRegions']"
             class="pi pi-map-marker text-blue-500"
             pTooltip="Uses diagram regions" tooltipPosition="right"
             style="font-size:0.8rem"></i>
        </td>
        <td>{{getOperationName(step)}}</td>
        <td>
          <span class="op-engine-badge" [class]="getEngineBadgeClass(step.engineId)">
            {{getEngineBadge(step.engineId)}}
          </span>
        </td>
        <td>
          <div class="flex gap-1 align-items-center">
            <button pButton pRipple icon="pi pi-arrow-up"
                    class="p-button-rounded p-button-text p-button-sm"
                    (click)="moveStep(ri, -1); $event.stopPropagation()"
                    [disabled]="ri === 0"></button>
            <button pButton pRipple icon="pi pi-arrow-down"
                    class="p-button-rounded p-button-text p-button-sm"
                    (click)="moveStep(ri, 1); $event.stopPropagation()"
                    [disabled]="ri === steps.length - 1"></button>
            <button pButton pRipple
                    [icon]="step.enabled ? 'pi pi-eye' : 'pi pi-eye-slash'"
                    class="p-button-rounded p-button-text p-button-sm"
                    pTooltip="Toggle step"
                    (click)="toggleStep(ri); $event.stopPropagation()"></button>
            <button pButton pRipple icon="pi pi-trash"
                    class="p-button-rounded p-button-text p-button-sm p-button-danger"
                    (click)="removeStep(ri); $event.stopPropagation()"></button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="5" class="text-center text-color-secondary p-3">
          Choose an operation above and click <i class="pi pi-plus"></i> to build the pipeline.
        </td>
      </tr>
    </ng-template>
  </p-table>

  <!-- Parameters panel for selected step -->
  <div *ngIf="selectedStep && getDescriptor(selectedStep)" class="step-params-panel mt-2">
    <h4 class="mt-2 mb-2">Parameters: {{getOperationName(selectedStep)}}</h4>

    <div *ngIf="getDescriptor(selectedStep)!.description"
         class="text-color-secondary text-sm mb-3">
      {{getDescriptor(selectedStep)!.description}}
    </div>

    <div *ngFor="let param of getDescriptor(selectedStep)!.params"
         class="field grid align-items-center my-2">
      <label class="col-12 md:col-5 mb-0 text-black-alpha-90">
        {{param.label}}
        <i *ngIf="param.tooltip" class="pi pi-info-circle px-1 text-color-secondary"
           [pTooltip]="param.tooltip" tooltipPosition="top"></i>
      </label>
      <div class="col-12 md:col-7" [ngSwitch]="param.type">
        <p-inputNumber *ngSwitchCase="'number'"
                       [ngModel]="selectedStep!.params[param.id]"
                       (ngModelChange)="onParamChange(param.id, $event)"
                       [ngModelOptions]="{standalone: true}"
                       [min]="param.min" [max]="param.max" [step]="param.step || 1"
                       [showButtons]="true" class="p-inputtext-sm">
        </p-inputNumber>
        <p-checkbox *ngSwitchCase="'boolean'"
                    [ngModel]="selectedStep!.params[param.id]"
                    (ngModelChange)="onParamChange(param.id, $event)"
                    [ngModelOptions]="{standalone: true}"
                    [binary]="true">
        </p-checkbox>
        <p-dropdown *ngSwitchCase="'select'"
                    [options]="param.options"
                    [ngModel]="selectedStep!.params[param.id]"
                    (ngModelChange)="onParamChange(param.id, $event)"
                    [ngModelOptions]="{standalone: true}"
                    class="p-inputtext-sm"
                    appendTo="body">
        </p-dropdown>
        <input *ngSwitchCase="'string'" pInputText class="p-inputtext-sm w-full"
               [ngModel]="selectedStep!.params[param.id]"
               (ngModelChange)="onParamChange(param.id, $event)"
               [ngModelOptions]="{standalone: true}"/>
      </div>
    </div>

    <!-- Region-aware toggle (shown only for eligible operations) -->
    <div *ngIf="isRegionAware(selectedStep)" class="field-checkbox mt-2">
      <p-checkbox
        [ngModel]="selectedStep!.params['useRegions']"
        (ngModelChange)="onParamChange('useRegions', $event)"
        [ngModelOptions]="{standalone: true}"
        [binary]="true"
        id="useRegions">
      </p-checkbox>
      <label for="useRegions" class="ml-2">
        Use diagram regions as input prompts
        <i class="pi pi-info-circle px-1 text-color-secondary"
           pTooltip="When enabled, the polygons drawn in the image viewer are forwarded
                     to the worker as input_regions (e.g. SAM point prompts). Draw regions
                     in the Regions panel before running."
           tooltipPosition="top"></i>
        <span *ngIf="selectedStep!.params['useRegions'] && !hasRegions()"
              class="text-orange-500 text-sm ml-2">
          <i class="pi pi-exclamation-triangle"></i> No regions drawn yet
        </span>
      </label>
    </div>
  </div>

  <!-- ─── File Destination ───────────────────────────────────────── -->
  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">File Destination</div>
  </p-divider>

  <!-- Output suffix -->
  <div class="field grid h-3rem align-items-center">
    <label for="outputSuffix" class="col-12 md:col-5 mb-0 text-black-alpha-90">
      Output suffix:
      <i class="pi pi-info-circle px-1"
         pTooltip="Appended to the source filename before the extension.
                   E.g. image_cvai.png"
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-7">
      <ng-container *ngIf="form.get('editOutputSuffix')?.value; else viewSuffix">
        <div class="output-folder-edit">
          <input id="outputSuffix" type="text" class="p-inputtext-sm w-full"
                 formControlName="outputSuffix" pInputText/>
          <i class="pi pi-times icon-hover"
             (click)="form.get('editOutputSuffix')?.setValue(false)"></i>
        </div>
      </ng-container>
      <ng-template #viewSuffix>
        <span class="text-black-alpha-90">{{form.get('outputSuffix')?.value}}</span>
        <i class="pi pi-pencil icon-hover ml-2"
           (click)="form.get('editOutputSuffix')?.setValue(true)"></i>
      </ng-template>
    </div>
  </div>

  <!-- Submit -->
  <div class="pt-3 pb-3 process-buttons flex align-items-center gap-3">
    <app-submit-button
      label="Run"
      [isSubmitting]="isSubmitting"
      [processName]="processName"
      [validationMessage]="enabledStepCount === 0 ? 'Add at least one operation.' : validationMessage"
      icon="pi pi-play"
      styleClass="p-button-primary"
      [tooltip]="'Run ' + processName">
    </app-submit-button>
    <button pButton type="button" label="Create Rule" icon="pi pi-bell" class="p-button-outlined"
            [disabled]="(canCreateRule$ | async) === false || enabledStepCount === 0 || form.invalid"
            pTooltip="Auto-run this on every new file in the selected folder."
            (click)="onCreateRule()"></button>
  </div>

</form>
